import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TRUST_BADGES } from "@/data/site";
import { useT } from "@/context/LanguageContext";
import { Icon } from "@/components/common/Icon";
import { ArrowRight, PlayCircle, X } from "@phosphor-icons/react";

const HERO_IMG = "https://images.pexels.com/photos/15674629/pexels-photo-15674629.jpeg";

function TrustBadges({ t }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.5 }}
      className="mt-14 grid max-w-3xl grid-cols-2 gap-x-8 gap-y-4 border-t border-white/10 pt-8 sm:grid-cols-4"
    >
      {TRUST_BADGES.map((b) => (
        <div key={b.key} className="flex items-center gap-2.5" data-testid="hero-trust-badge">
          <Icon name={b.icon} className="text-teal-light shrink-0" size={24} />
          <span className="text-xs font-medium leading-tight text-slate-200">{t(`trust.${b.key}`)}</span>
        </div>
      ))}
    </motion.div>
  );
}

function VideoModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-5" onClick={onClose}>
      <div className="relative aspect-video w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white" data-testid="hero-video-close">
          <X size={28} />
        </button>
        <iframe
          className="h-full w-full rounded-lg"
          src="https://www.youtube.com/embed/dQw4w9WgXcQ"
          title="How it works"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
}

export function Hero() {
  const { t } = useT();
  const [showVideo, setShowVideo] = useState(false);
  return (
    <section className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      <motion.img
        src={HERO_IMG}
        alt="Container ship sailing global trade routes"
        initial={{ scale: 1.12 }}
        animate={{ scale: 1 }}
        transition={{ duration: 12, ease: "easeOut" }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F]/95 via-[#0A192F]/80 to-[#0A192F]/40" />
      <div className="absolute inset-0 grain opacity-[0.15]" />

      <div className="relative mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-5 pt-28 pb-16 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-xs uppercase tracking-[0.3em] font-semibold text-teal-light"
        >
          {t("hero.overline")}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-5 max-w-4xl font-heading text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-7xl text-balance"
        >
          {t("hero.titlePre")}
          <span className="bg-gradient-to-r from-[#3FBF93] to-[#2BA8E0] bg-clip-text text-transparent">
            {t("hero.titleHighlight")}
          </span>
          {t("hero.titlePost")}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-200"
        >
          {t("hero.subtitle")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contact?type=quote"
            data-testid="hero-cta-quote"
            className="group flex items-center gap-2 rounded-md bg-gradient-to-r from-[#1E73BE] to-[#16B894] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-0.5"
          >
            {t("hero.ctaQuote")}
            <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/contact?type=consultation"
            data-testid="hero-cta-consult"
            className="rounded-md border border-white/30 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/10"
          >
            {t("hero.ctaConsult")}
          </Link>
          <button
            onClick={() => setShowVideo(true)}
            data-testid="hero-watch-video"
            className="flex items-center gap-2 text-sm font-semibold text-white/90 transition-colors hover:text-teal-light"
          >
            <PlayCircle size={26} weight="fill" /> {t("hero.watch")}
          </button>
        </motion.div>

        <TrustBadges t={t} />
      </div>

      {showVideo && <VideoModal onClose={() => setShowVideo(false)} />}
    </section>
  );
}
